import { useState, useEffect } from "react";
import { listTriggers, createTrigger, updateTrigger, deleteTrigger, reorderTriggers } from "../lib/adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, X, Check, GripVertical, ArrowUp, ArrowDown, Lock } from "lucide-react";

const EMPTY = {
  trigger_id: "",
  trigger_name: "",
  description: "",
  is_active: true,
  display_order: 0,
  forces_ewm_advanced: false,
  requires_non_low_complexity: false,
  protected: false,
};

export default function TriggersPage() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [msg, setMsg] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => listTriggers()
    .then(arr => setItems((arr || []).sort((a, b) => (a.display_order || 0) - (b.display_order || 0))))
    .catch(() => {});
  useEffect(() => { load(); }, []);

  const showMsg = (t) => { setMsg(t); setTimeout(() => setMsg(""), 2500); };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY, display_order: items.length });
    setShowForm(true);
  };
  const openEdit = (t) => {
    setEditing(t.id);
    setForm({ ...EMPTY, ...t });
    setShowForm(true);
  };
  const cancel = () => { setShowForm(false); setEditing(null); setForm({ ...EMPTY }); };

  const save = async () => {
    if (!form.trigger_name.trim()) { showMsg("Trigger name is required"); return; }
    try {
      if (editing) await updateTrigger(editing, form);
      else await createTrigger(form);
      setShowForm(false); setEditing(null); setForm({ ...EMPTY });
      showMsg("Trigger saved");
      load();
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error saving trigger");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTrigger(deleteTarget);
      setDeleteTarget(null);
      showMsg("Trigger deleted — any questions using it were reset to None");
      load();
    } catch (e) {
      showMsg(e?.response?.data?.detail || "Error deleting trigger");
    }
  };

  const persistOrder = async (orderedIds, optimistic) => {
    if (optimistic) setItems(optimistic);
    setBusy(true);
    try {
      await reorderTriggers(orderedIds);
      await load();
      showMsg("Order updated");
    } catch {
      showMsg("Failed to save order");
      await load();
    } finally {
      setBusy(false);
    }
  };
  const moveUp = (idx) => {
    if (idx <= 0 || busy) return;
    const next = [...items];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    persistOrder(next.map(t => t.id), next);
  };
  const moveDown = (idx) => {
    if (idx >= items.length - 1 || busy) return;
    const next = [...items];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    persistOrder(next.map(t => t.id), next);
  };

  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch (_) {}
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== dragOverId) setDragOverId(id);
  };
  const onDrop = (e, targetId) => {
    e.preventDefault();
    const sourceId = dragId;
    setDragId(null); setDragOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const from = items.findIndex(t => t.id === sourceId);
    const to = items.findIndex(t => t.id === targetId);
    if (from < 0 || to < 0) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    persistOrder(next.map(t => t.id), next);
  };
  const onDragEnd = () => { setDragId(null); setDragOverId(null); };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>Trigger Management</h1>
        <Button data-testid="add-trigger-btn" onClick={openAdd} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Trigger
        </Button>
      </div>
      <p className="text-xs text-[#9CA3AF] mb-5">
        Master list of advanced capability triggers used by questions and the recommendation engine.
        Deactivating or deleting a trigger automatically resets any questions referencing it to "None".
      </p>

      {msg && <div data-testid="trigger-msg" className="mb-3 px-3 py-2 bg-[#E0F2FE] border border-[#025979]/20 rounded-sm text-xs text-[#025979] font-medium">{msg}</div>}

      {showForm && (
        <div className="bg-white border border-[#025979]/20 rounded-sm shadow-sm mb-5 p-5" data-testid="trigger-form">
          <h2 className="text-sm font-semibold text-[#111827] mb-4" style={{ fontFamily: "Chivo" }}>
            {editing ? "Edit Trigger" : "Add New Trigger"}
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-[#374151] mb-1.5 block">Trigger Name <span className="text-red-500">*</span></label>
                <Input
                  data-testid="trigger-name-input"
                  value={form.trigger_name}
                  onChange={e => setForm({ ...form, trigger_name: e.target.value })}
                  className="rounded-sm text-sm"
                  placeholder="e.g. Advanced Picking"
                  disabled={form.protected}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#374151] mb-1.5 block">
                  Trigger ID <span className="text-[#9CA3AF] font-normal">(auto-slug if blank; immutable after save)</span>
                </label>
                <Input
                  data-testid="trigger-id-input"
                  value={form.trigger_id}
                  onChange={e => setForm({ ...form, trigger_id: e.target.value })}
                  className="rounded-sm text-xs font-mono"
                  placeholder="has_advanced_picking_requirement"
                  disabled={!!editing}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#374151] mb-1.5 block">Description</label>
              <Input
                data-testid="trigger-description-input"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                className="rounded-sm text-sm"
                placeholder="Short clarification of when this trigger applies"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <label className="text-xs font-medium text-[#374151] flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="trigger-active-checkbox"
                  type="checkbox"
                  checked={form.is_active}
                  onChange={e => setForm({ ...form, is_active: e.target.checked })}
                  disabled={form.protected}
                  className="rounded-sm"
                />
                Active
              </label>
              <label className="text-xs font-medium text-[#374151] flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="trigger-ewm-checkbox"
                  type="checkbox"
                  checked={form.forces_ewm_advanced}
                  onChange={e => setForm({ ...form, forces_ewm_advanced: e.target.checked })}
                  className="rounded-sm"
                />
                Forces EWM Advanced
              </label>
              <label className="text-xs font-medium text-[#374151] flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="trigger-nonlow-checkbox"
                  type="checkbox"
                  checked={form.requires_non_low_complexity}
                  onChange={e => setForm({ ...form, requires_non_low_complexity: e.target.checked })}
                  className="rounded-sm"
                />
                Requires Non-Low Complexity
              </label>
            </div>
          </div>

          <div className="mt-5 flex gap-2 border-t border-[#E5E7EB] pt-4">
            <Button data-testid="save-trigger-btn" onClick={save} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs px-5">
              <Check className="w-3 h-3 mr-1.5" />{editing ? "Update Trigger" : "Save Trigger"}
            </Button>
            <Button data-testid="cancel-trigger-btn" variant="outline" onClick={cancel} className="rounded-sm text-xs border-[#D1D5DB] text-[#6B7280] hover:text-[#111827]">
              <X className="w-3 h-3 mr-1" />Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden" data-testid="trigger-table">
        <div className="px-4 py-3 bg-gray-50 border-b border-[#E5E7EB] flex items-center justify-between">
          <p className="text-xs font-medium text-[#111827]">Master Triggers</p>
          <p className="text-[10px] text-[#9CA3AF]">{items.length} trigger{items.length !== 1 ? "s" : ""}</p>
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[#E5E7EB]">
              <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-20">Reorder</th>
              <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-8">#</th>
              <th className="text-left px-3 py-2 text-[#6B7280] font-medium">Trigger Name</th>
              <th className="text-left px-3 py-2 text-[#6B7280] font-medium">Trigger ID</th>
              <th className="text-left px-3 py-2 text-[#6B7280] font-medium">Description</th>
              <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-16">EWM Adv</th>
              <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-10">On</th>
              <th className="text-right px-3 py-2 text-[#6B7280] font-medium w-16"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((t, idx) => {
              const isDragging = dragId === t.id;
              const isDropTarget = dragOverId === t.id && dragId && dragId !== t.id;
              return (
                <tr
                  key={t.id}
                  data-testid={`trigger-row-${t.id}`}
                  draggable={!busy}
                  onDragStart={(e) => onDragStart(e, t.id)}
                  onDragOver={(e) => onDragOver(e, t.id)}
                  onDrop={(e) => onDrop(e, t.id)}
                  onDragEnd={onDragEnd}
                  className={`border-b border-[#E5E7EB] hover:bg-gray-50 transition-colors ${isDragging ? "opacity-40" : ""} ${isDropTarget ? "bg-[#E0F2FE] border-t-2 border-t-[#025979]" : ""}`}
                >
                  <td className="px-2 py-2.5">
                    <div className="flex items-center justify-center gap-1">
                      <span data-testid={`trigger-drag-${t.id}`} className="cursor-grab active:cursor-grabbing text-[#9CA3AF] hover:text-[#025979]"><GripVertical className="w-3.5 h-3.5" /></span>
                      <button data-testid={`trigger-up-${t.id}`} onClick={() => moveUp(idx)} disabled={idx === 0 || busy} className="text-[#6B7280] hover:text-[#025979] disabled:text-[#E5E7EB] disabled:cursor-not-allowed"><ArrowUp className="w-3.5 h-3.5" /></button>
                      <button data-testid={`trigger-down-${t.id}`} onClick={() => moveDown(idx)} disabled={idx === items.length - 1 || busy} className="text-[#6B7280] hover:text-[#025979] disabled:text-[#E5E7EB] disabled:cursor-not-allowed"><ArrowDown className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-[#9CA3AF]">{idx + 1}</td>
                  <td className="px-3 py-2.5 text-[#111827] font-medium">
                    <div className="flex items-center gap-1.5">
                      {t.protected && <Lock className="w-3 h-3 text-[#9CA3AF]" />}
                      {t.trigger_name}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-[#6B7280] font-mono text-[10px]">{t.trigger_id}</td>
                  <td className="px-3 py-2.5 text-[#6B7280] text-[10px] truncate max-w-[240px]" title={t.description}>{t.description || "—"}</td>
                  <td className="px-2 py-2.5 text-center">
                    {t.forces_ewm_advanced ? <span className="inline-block px-1.5 py-0.5 bg-[#E0F2FE] text-[#025979] rounded-sm text-[10px] font-medium">Yes</span> : <span className="text-[#D1D5DB]">—</span>}
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${t.is_active ? "bg-[#025979]" : "bg-[#D1D5DB]"}`} />
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button data-testid={`edit-trigger-${t.id}`} onClick={() => openEdit(t)} className="text-[#025979] hover:text-[#03738C] mr-2" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                    {!t.protected && (
                      <button data-testid={`delete-trigger-${t.id}`} onClick={() => setDeleteTarget(t.id)} className="text-red-400 hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-[#9CA3AF]">No triggers configured yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Trigger</DialogTitle>
            <DialogDescription>
              Deleting this trigger will reset any questions currently mapped to it back to "None". This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="rounded-sm text-xs">Cancel</Button>
            <Button data-testid="confirm-delete-trigger-btn" onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white rounded-sm text-xs">Delete Trigger</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
